import React, { useEffect, useState } from "react";
import Card from "components/card";
import { MdCheckCircle, MdCancel } from "react-icons/md";
import { useUnidades } from "context/UnidadeContext";
import { useDevices, Device } from "context/DeviceContext";
import { useAprendizado } from "context/AprendizadoContext";
import { useSelectedDevice } from "context/SelectedDeviceContext";
import { useRelatorios } from "context/RelatorioContext";

interface MetasUnidadesProps {
  selectedUnidadeId: string | null;
  setSelectedUnidadeId: React.Dispatch<React.SetStateAction<string | null>>;
}

export default function MetasUnidades({
  selectedUnidadeId,
  setSelectedUnidadeId,
}: MetasUnidadesProps) {
  const { unidades } = useUnidades();
  const { devices } = useDevices();
  const { aprendizadoData } = useAprendizado();
  const { relatorios } = useRelatorios();
  const { setSelectedDeviceId } = useSelectedDevice();
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  const calcularSemana = (dataCadastro: string) => {
    const inicio = new Date(dataCadastro);
    const diff = Math.floor((new Date().getTime() - inicio.getTime()) / (1000 * 60 * 60 * 24));
    return Math.min(4, Math.floor(diff / 7) + 1);
  };

  const getExpedienteAtual = (device?: Device | null) => {
    if (!device || !device.expedientes) return null;
    const agoraMins = currentTime.getHours() * 60 + currentTime.getMinutes();

    for (const [nome, horario] of Object.entries(device.expedientes)) {
      if (horario.start && horario.end) {
        const [startH, startM] = horario.start.split(':').map(Number);
        const [endH, endM] = horario.end.split(':').map(Number);
        const startMins = startH * 60 + startM;
        const endMins = endH * 60 + endM;

        if (startMins < endMins) {
          // Expediente Diurno
          if (agoraMins >= startMins && agoraMins <= endMins) return nome;
        } else {
          // Expediente Noturno (cruza a meia-noite)
          if (agoraMins >= startMins || agoraMins <= endMins) return nome;
        }
      }
    }
    return null;
  };

  const verificarOperando = (device: Device): boolean => {
    const dia = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sab"][currentTime.getDay()];
    const noDia = device.days.includes(dia);
    const noTurno = getExpedienteAtual(device) !== null;
    return noDia && noTurno;
  };

  const calcularMetaComBaseNoHistorico = (dispositivoId: string, dataCadastro: string, nomeExpediente: string | null) => {
    if (!nomeExpediente) return null;

    const diasDaSemana = ["dom.", "seg.", "ter.", "qua.", "qui.", "sex.", "sab."];
    const hojeStr = diasDaSemana[currentTime.getDay()];
    const dataInicioAprendizado = new Date(dataCadastro);
    const dataFimAprendizado = new Date(dataInicioAprendizado);
    dataFimAprendizado.setDate(dataInicioAprendizado.getDate() + 28);

    const relatoriosAprendizado = relatorios.filter(rel =>
      rel.dispositivoId === dispositivoId &&
      rel.DiaSemana === hojeStr &&
      rel.expediente === nomeExpediente &&
      new Date(rel.Data) >= dataInicioAprendizado &&
      new Date(rel.Data) <= dataFimAprendizado
    );

    if (relatoriosAprendizado.length === 0) return null;

    const somaContadores = relatoriosAprendizado.reduce((sum, r) => sum + r.contador, 0);
    const metaMedia = somaContadores / relatoriosAprendizado.length;

    return Math.round(metaMedia);
  };

  const handleSelect = (unidadeId: string, dispositivoId: string) => {
    if (selectedUnidadeId === unidadeId) {
      setSelectedUnidadeId(null);
      setSelectedDeviceId(null);
    } else {
      setSelectedUnidadeId(unidadeId);
      setSelectedDeviceId(dispositivoId);
    }
  };

  return (
    <Card extra="w-full p-4 h-full">
      <h4 className="text-xl font-bold text-navy-700 mb-4">
        Unidades
      </h4>

      {unidades.length === 0 ? (
        <div className="text-gray-500 text-center py-8">
          Nenhuma unidade encontrada.
        </div>
      ) : (
        <div className="overflow-y-auto max-h-[280px]">
          <table className="w-full text-left">
            <thead>
              <tr className="text-sm font-bold text-gray-600">
                <th>Unidade</th>
                <th>Operação</th>
                <th>Data</th>
                <th>Contagem</th>
              </tr>
            </thead>
            <tbody>
              {unidades.map((u) => {
                const device = devices.find((dev) => dev.id === u.dispositivoId);
                const aprendizado = aprendizadoData.find((a) => a.dispositivoId === u.dispositivoId);
                const semana = aprendizado ? calcularSemana(aprendizado.dataCadastro) : 1;
                const aprendendo = semana < 4;

                const expedienteAtualNome = device ? getExpedienteAtual(device) : null;
                const operando = device ? verificarOperando(device) : false;

                let metaMaximaFinal = null;
                if (!aprendendo && aprendizado && expedienteAtualNome) {
                  metaMaximaFinal = calcularMetaComBaseNoHistorico(u.dispositivoId, aprendizado.dataCadastro, expedienteAtualNome);
                }

                const progresso = metaMaximaFinal ? Math.round((u.contagemAtual / metaMaximaFinal) * 100) : 0;

                const getProgressColor = () => {
                  if (progresso === 0) return "bg-gray-300";
                  if (progresso < 60) return "bg-red-500";
                  if (progresso < 90) return "bg-yellow-500";
                  if (progresso < 120) return "bg-green-500";
                  return "bg-red-500";
                };

                const contagemExibir = aprendendo
                  ? <span className="text-sm font-bold text-navy-700">{u.contagemAtual} / Aprendendo...</span>
                  : (
                    <div className="w-full">
                      <div className="flex justify-between mb-1 text-xs">
                        <span>{u.contagemAtual} / {metaMaximaFinal ?? "--"}</span>
                        <span>{metaMaximaFinal ? progresso : 0}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full transition-all duration-300 ${getProgressColor()}`}
                          style={{
                            width: `${Math.min(100, metaMaximaFinal ? (u.contagemAtual / metaMaximaFinal * 100) : 0)}%`
                          }}
                        ></div>
                      </div>
                    </div>
                  );

                return (
                  <tr
                    key={u.id}
                    onClick={() => handleSelect(u.id, u.dispositivoId)}
                    className={`cursor-pointer border-t transition-colors ${selectedUnidadeId === u.id ? "bg-gray-100" : ""
                      }`}
                  >
                    <td className="text-sm font-bold text-navy-700 py-3">
                      {device?.name || u.nome}
                    </td>
                    <td className="text-sm font-bold text-navy-700">
                      <div className="flex items-center gap-1">
                        {operando ? (
                          <MdCheckCircle className="text-green-500" />
                        ) : (
                          <MdCancel className="text-red-500" />
                        )}
                      </div>
                    </td>
                    <td className="text-sm font-bold text-navy-700">
                      {new Date().toLocaleDateString("pt-BR")}
                    </td>
                    <td className="text-sm font-bold text-navy-700">
                      {contagemExibir}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
